import { useCallback, useEffect, useRef } from "react";
import Lenis from "lenis";
import { ScrollTrigger } from "../animations/gsap";

const DEFAULT_OFFSET = -96;

const useLenis = () => {
    const lenisRef = useRef<Lenis | null>(null);

    useEffect(() => {
        const lenis = new Lenis({
            lerp: 0.08,
            smoothWheel: true,
            wheelMultiplier: 0.9,
        });

        lenisRef.current = lenis;
        lenis.on("scroll", ScrollTrigger.update);
        ScrollTrigger.refresh();

        let rafId = 0;
        const raf = (time: number) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    const scrollTo = useCallback(
        (target: string | HTMLElement, offset = DEFAULT_OFFSET) => {
            const lenis = lenisRef.current;
            const resolvedTarget =
                typeof target === "string" ? target : target;

            if (lenis) {
                lenis.scrollTo(resolvedTarget, {
                    offset,
                    duration: 1.1,
                    easing: (t: number) => 1 - Math.pow(1 - t, 3),
                });
                return;
            }

            if (typeof resolvedTarget === "string") {
                const element = document.querySelector(resolvedTarget);
                element?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }

            resolvedTarget.scrollIntoView({ behavior: "smooth", block: "start" });
        },
        []
    );

    return { lenis: lenisRef.current, scrollTo };
};

export default useLenis;
